import NoScheduledEventsBox from "./NoScheduledEventsBox"
import NoFilteredEventsBox from "./NoFilteredEventsBox"
import DayViewMap from "./DayViewMap"
import { useDayViewEventsQuery, useConsultationsByDateRangeQuery, useTasksByDateRangeQuery } from "../scheduleQueries"
import { ScheduleState, UNASSIGNED_CREW_ID } from "../scheduleModel"
import { filterConsultations, filterEvents, filterTasks } from '../scheduleFilter'
import { Spin } from "antd"
import DayViewEventList from "./DayViewEventList"
import { useSchedule } from "../ScheduleContext"
import { useDroppable } from "@dnd-kit/core"
import { useState, useMemo } from "react"
import dayjs from "dayjs"
import EditConsultationModal from "../EditConsultation/EditConsultationModal"
import TaskEventModal from "../EditTask/TaskEventModal"
import { ConsultationCalendarEvent, TaskCalendarEvent } from "../scheduleApi"

export type Props = {
  state: ScheduleState
  onEnterReorderMode: (selectedCrewLeadId: number | typeof UNASSIGNED_CREW_ID) => void
}

const ScheduleDayView = ({ state, onEnterReorderMode }: Props) => {
  const { selectedDate } = state
  const dayViewQuery = useDayViewEventsQuery(selectedDate.format('YYYY-MM-DD'))
  const consultationsQuery = useConsultationsByDateRangeQuery(
    selectedDate.format('YYYY-MM-DD'),
    selectedDate.format('YYYY-MM-DD')
  )
  const tasksQuery = useTasksByDateRangeQuery(
    selectedDate.format('YYYY-MM-DD'),
    selectedDate.format('YYYY-MM-DD')
  )
  const { crew } = useSchedule()
  const [hoveredEventId, setHoveredEventId] = useState<number | null>(null)
  const [hoveredEventIds, setHoveredEventIds] = useState<number[]>([])
  const [selectedConsultation, setSelectedConsultation] = useState<ConsultationCalendarEvent | null>(null)
  const [consultationModalOpen, setConsultationModalOpen] = useState(false)
  const [selectedTask, setSelectedTask] = useState<TaskCalendarEvent | null>(null)
  const [taskModalOpen, setTaskModalOpen] = useState(false)

  const events = dayViewQuery.data?.events ?? []
  const allConsultations = consultationsQuery.data?.consultations ?? []

  const consultations = useMemo(() => {
    const dateKey = selectedDate.format('YYYY-MM-DD')
    const dayConsultations = allConsultations.filter(c => dayjs(c.start).format('YYYY-MM-DD') === dateKey)
    return filterConsultations(dayConsultations, state.filters, crew ?? [])
  }, [allConsultations, selectedDate, state.filters, crew])
  const visits = dayViewQuery.data?.visits ?? []
  const calendarEventVisits = dayViewQuery.data?.calendarEventVisits ?? []
  const visitWorkareaStatuses = dayViewQuery.data?.visitWorkareaStatuses ?? []
  const timesheets = dayViewQuery.data?.timesheets ?? []

  const scheduledEvents = events.length > 0 ? events.sort((a, b) => {
    if (a.start === b.start) {
      return 0
    }
    return a.start > b.start ? 1 : -1
  }) : []

  const filteredEvents = useMemo(() => {
    return filterEvents(scheduledEvents, state.filters, visits, visitWorkareaStatuses, timesheets, crew ?? [], calendarEventVisits, state.visibleDateRange, new Map())
  }, [scheduledEvents, state.filters, visits, visitWorkareaStatuses, timesheets, crew, calendarEventVisits, state.visibleDateRange])

  const { setNodeRef, isOver } = useDroppable({
    id: `day-${selectedDate.format('YYYY-MM-DD')}`,
    data: { date: selectedDate },
  })

  const filteredTasks = useMemo(() => {
    return filterTasks(tasksQuery.data?.tasks ?? [], state.filters, crew ?? [])
  }, [tasksQuery.data, state.filters, crew])

  const handleConsultationClick = (consultation: ConsultationCalendarEvent) => {
    setSelectedConsultation(consultation)
    setConsultationModalOpen(true)
  }

  const handleTaskClick = (event: TaskCalendarEvent) => {
    setSelectedTask(event)
    setTaskModalOpen(true)
  }

  return <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
    {/* Left Panel */}
    <div ref={setNodeRef} style={{ width: 500, background: isOver ? '#e6f7ff' : '#fafbfc', display: 'flex', flexDirection: 'column', overflow: 'scroll', transition: 'background 0.2s' }}>
      {dayViewQuery.isLoading &&
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', gap: '8px' }}><Spin size="large" /><div>Loading...</div></div>}
      {!dayViewQuery.isLoading && !consultationsQuery.isLoading && scheduledEvents.length === 0 && consultations.length === 0 && filteredTasks.length === 0 &&
        <NoScheduledEventsBox />}
      {!dayViewQuery.isLoading && !consultationsQuery.isLoading && scheduledEvents.length > 0 && filteredEvents.length === 0 && consultations.length === 0 && filteredTasks.length === 0 &&
        <NoFilteredEventsBox />}
      {!dayViewQuery.isLoading && !consultationsQuery.isLoading && (filteredEvents.length > 0 || consultations.length > 0 || filteredTasks.length > 0) &&
        <DayViewEventList
          events={filteredEvents}
          consultations={consultations}
          tasks={filteredTasks}
          visits={visits}
          calendarEventVisits={calendarEventVisits}
          visitWorkareaStatuses={visitWorkareaStatuses}
          timesheets={timesheets}
          hoveredEventId={hoveredEventId}
          hoveredEventIds={hoveredEventIds}
          setHoveredEventId={setHoveredEventId}
          setHoveredEventIds={setHoveredEventIds}
          onConsultationClick={handleConsultationClick}
          onTaskClick={handleTaskClick}
          crew={crew ?? []}
          date={selectedDate}
          onEnterReorderMode={onEnterReorderMode} />}
    </div>
    {/* Map Panel */}
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <DayViewMap
        events={filteredEvents}
        visits={visits}
        calendarEventVisits={calendarEventVisits}
        workareaStatuses={visitWorkareaStatuses}
        timesheets={timesheets}
        date={selectedDate}
        hoveredEventId={hoveredEventId}
        hoveredEventIds={hoveredEventIds}
      />
    </div>

    <EditConsultationModal
      open={consultationModalOpen}
      onClose={() => {
        setConsultationModalOpen(false)
        setSelectedConsultation(null)
      }}
      consultation={selectedConsultation}
    />

    <TaskEventModal
      open={taskModalOpen}
      onClose={() => {
        setTaskModalOpen(false)
        setSelectedTask(null)
      }}
      event={selectedTask}
    />
  </div>
}

export default ScheduleDayView